// M004-F0103 / MP004-D006 §5.1.2 / WebUI issue #15
//
// `/app/panoptic` must resolve its active organisation the same way
// `/app/admin/rooms` already does: the allowed-organisation list is
// filtered the same way (`selectAdminRoomsOrganisations`), and the default
// selection is deterministically the first allowed organisation in that
// filtered list -- never an unfiltered first-with-id from the raw
// `GET /api/organisations/mine` response (the WebUI #15 defect).
//
// PanOptic reuses that Admin Rooms selection idiom directly rather than
// reimplementing it, with ONE deliberate divergence: PanOptic never offers
// an "All Organisations" choice, for any role including a `super_admin`
// with `organisation_view_all`. The health strip, camera matrix
// (`MP004-F0423`), watch-rule drawer (`MP004-F0457`), and NVR Camera Matrix
// cards (`MP004-F0458`) are all single-organisation views; silently
// aggregating them across organisations would misattribute cameras, feeds,
// and watch rules to the wrong org. See MP004-D006 §5.1.2 for the full
// requirement text.

import { selectAdminRoomsOrganisations, isGlobalSuperAdmin } from '../admin/adminRoomsOrganisationScope';

const stringId = (value) => (value === undefined || value === null ? '' : String(value));

const organisationId = (organisation = {}) => stringId(
  organisation.id ?? organisation.organisation_id ?? organisation.organisationId,
);

// Reused as-is from Admin Rooms: a `super_admin` sees every organisation the
// `organisation_view_all` capability grants; any other role sees only
// memberships that grant admin-equivalent access to that organisation. The
// organisation list is a display/request-selection boundary, not an
// authorisation boundary -- Core remains authoritative.
export const selectPanopticOrganisations = (organisations, user) => (
  selectAdminRoomsOrganisations(organisations, user)
);

// True whenever the resolved session can see more than one allowed
// organisation. A session with exactly one allowed organisation renders no
// selector; the sole organisation is the implicit, non-editable selection.
export const shouldShowPanopticOrganisationSelector = (organisations, user) => (
  selectPanopticOrganisations(organisations, user).length > 1
);

// Deterministic first-allowed-organisation default, plus fail-closed
// recovery from a stale, out-of-scope, or forged selection -- the same
// M004-F0103 rule Admin Rooms uses for `resolveAdminRoomsSelection`, minus
// its super_admin "All Organisations" (`''`) branch. PanOptic must never
// return an empty/all-organisations selection for any role: a `super_admin`
// who can see more than one organisation gets the same page-level selector
// everyone else does, defaulted to the first allowed organisation.
export const resolvePanopticOrganisationSelection = (selection, organisations, user) => {
  const selectedId = stringId(selection);
  const allowedOrganisations = selectPanopticOrganisations(organisations, user);

  if (allowedOrganisations.some((organisation) => organisationId(organisation) === selectedId)) {
    return selectedId;
  }

  return organisationId(allowedOrganisations[0]);
};

export { isGlobalSuperAdmin };
